import React, { useState, useEffect } from 'react';
import { Calendar, User, Phone, Mail, Clock, FileText, Eye, CheckCircle, AlertCircle, LogOut } from 'lucide-react';
import axios from 'axios';
import AdminLogin from '../components/AdminLogin';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminUser, setAdminUser] = useState('');

  useEffect(() => {
    // Check for existing authentication
    const authStatus = sessionStorage.getItem('adminAuth');
    const user = sessionStorage.getItem('adminUser');
    const loginTime = sessionStorage.getItem('loginTime');
    
    // Check if login is still valid (24 hours)
    if (authStatus && user && loginTime) {
      const loginDate = new Date(loginTime);
      const now = new Date();
      const hoursDiff = (now - loginDate) / (1000 * 60 * 60);
      
      if (hoursDiff < 24) {
        setIsAuthenticated(true);
        setAdminUser(user);
        fetchAppointments();
      } else {
        // Session expired
        handleLogout();
      }
    }
  }, []);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/appointments`);
      setAppointments(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch appointments');
      console.error('Error fetching appointments:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (success) => {
    if (success) {
      setIsAuthenticated(true);
      setAdminUser(sessionStorage.getItem('adminUser'));
      fetchAppointments();
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuth');
    sessionStorage.removeItem('adminUser');
    sessionStorage.removeItem('loginTime');
    setIsAuthenticated(false);
    setAdminUser('');
    setAppointments([]);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      confirmed: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      cancelled: { color: 'bg-red-100 text-red-800', icon: AlertCircle }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        <Icon size={14} className="mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getReasonColor = (reason) => {
    const colors = {
      'General Consultation': 'bg-blue-50 text-blue-700 border-blue-200',
      'Weight Loss Surgery Consultation': 'bg-purple-50 text-purple-700 border-purple-200',
      'Gallbladder Consultation': 'bg-green-50 text-green-700 border-green-200',
      'Hernia Consultation': 'bg-orange-50 text-orange-700 border-orange-200'
    };
    return colors[reason] || 'bg-gray-50 text-gray-700 border-gray-200';
  };

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading appointments...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto mb-4 text-red-500" size={48} />
          <p className="text-red-600">{error}</p>
          <button 
            onClick={fetchAppointments}
            className="mt-4 btn btn-primary"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      <div className="container mx-auto py-8 px-4">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="heading-primary text-3xl mb-2">Admin Dashboard</h1>
              <p className="text-gray-600">Manage patient appointments for Professor Vasha Kaur</p>
              <p className="text-sm text-blue-600">Logged in as: {adminUser}</p>
            </div>
            <div className="flex gap-3">
              <button 
                onClick={fetchAppointments}
                className="btn btn-secondary"
                data-testid="refresh-btn"
              >
                Refresh
              </button>
              <button 
                onClick={handleLogout}
                className="btn btn-secondary"
                data-testid="logout-btn"
              >
                <LogOut size={16} className="mr-2" />
                Logout
              </button>
            </div>
          </div>
          
          {/* Stats */}
          <div className="grid grid-3 gap-6 mt-8">
            <div className="stat-card">
              <div className="stat-number text-blue-600">{appointments.length}</div>
              <div className="stat-label">Total Appointments</div>
            </div>
            <div className="stat-card">
              <div className="stat-number text-yellow-600">
                {appointments.filter(apt => apt.status === 'pending').length}
              </div>
              <div className="stat-label">Pending</div>
            </div>
            <div className="stat-card">
              <div className="stat-number text-green-600">
                {appointments.filter(apt => apt.status === 'confirmed').length}
              </div>
              <div className="stat-label">Confirmed</div>
            </div>
          </div>
        </div>

        {/* Appointments List */}
        <div className="card">
          <div className="p-6">
            <h2 className="heading-tertiary mb-6">Appointment Requests</h2>
            
            {appointments.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="mx-auto mb-4 text-gray-400" size={64} />
                <p className="text-gray-500">No appointments found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {appointments.map((appointment) => (
                  <div 
                    key={appointment.id} 
                    className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                    data-testid={`appointment-${appointment.id}`}
                  >
                    <div className="grid grid-2 gap-6">
                      {/* Left Column */}
                      <div>
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-1">
                              {appointment.first_name} {appointment.last_name}
                            </h3>
                            {getStatusBadge(appointment.status)}
                          </div>
                        </div>
                        
                        <div className="space-y-3">
                          <div className="flex items-center gap-3">
                            <Mail className="text-gray-400" size={16} />
                            <span className="text-gray-700">{appointment.email}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <Phone className="text-gray-400" size={16} />
                            <span className="text-gray-700">{appointment.phone}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <User className="text-gray-400" size={16} />
                            <span className="text-gray-700">DOB: {formatDate(appointment.date_of_birth)}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Right Column */}
                      <div>
                        <div className="space-y-4">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <Calendar className="text-blue-500" size={16} />
                              <span className="font-medium">Preferred Appointment</span>
                            </div>
                            <p className="text-lg font-semibold text-blue-600">
                              {formatDate(appointment.preferred_date)} at {appointment.preferred_time}
                            </p>
                          </div>
                          
                          <div>
                            <span className={`inline-block px-3 py-1 rounded-lg text-sm font-medium border ${getReasonColor(appointment.reason)}`}>
                              {appointment.reason}
                            </span>
                          </div>
                          
                          {appointment.notes && (
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <FileText className="text-gray-400" size={16} />
                                <span className="font-medium text-gray-700">Notes</span>
                              </div>
                              <p className="text-gray-600 text-sm bg-gray-50 p-3 rounded-lg">
                                {appointment.notes}
                              </p>
                            </div>
                          )}
                          
                          <div className="text-xs text-gray-500">
                            Submitted: {formatDateTime(appointment.created_at)}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="mt-4 pt-4 border-t border-gray-200 flex gap-3">
                      <button 
                        className="btn btn-primary text-sm"
                        onClick={() => setSelectedAppointment(appointment)}
                        data-testid={`view-details-${appointment.id}`}
                      >
                        <Eye size={14} className="mr-1" />
                        View Details
                      </button>
                      <a 
                        href={`mailto:${appointment.email}?subject=Appointment Confirmation&body=Dear ${appointment.first_name}, Thank you for your appointment request...`}
                        className="btn btn-secondary text-sm"
                        data-testid={`email-patient-${appointment.id}`}
                      >
                        <Mail size={14} className="mr-1" />
                        Email Patient
                      </a>
                      <a 
                        href={`tel:${appointment.phone}`}
                        className="btn btn-secondary text-sm"
                        data-testid={`call-patient-${appointment.id}`}
                      >
                        <Phone size={14} className="mr-1" />
                        Call Patient
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedAppointment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex items-start justify-between mb-6">
                <h3 className="heading-tertiary">Appointment Details</h3>
                <button 
                  onClick={() => setSelectedAppointment(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Patient Name</label>
                    <p className="text-lg">{selectedAppointment.first_name} {selectedAppointment.last_name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Email</label>
                    <p>{selectedAppointment.email}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Phone</label>
                    <p>{selectedAppointment.phone}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Date of Birth</label>
                    <p>{formatDate(selectedAppointment.date_of_birth)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Preferred Date</label>
                    <p>{formatDate(selectedAppointment.preferred_date)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Preferred Time</label>
                    <p>{selectedAppointment.preferred_time}</p>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Reason for Visit</label>
                  <p>{selectedAppointment.reason}</p>
                </div>
                
                {selectedAppointment.notes && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Additional Notes</label>
                    <p className="bg-gray-50 p-3 rounded-lg">{selectedAppointment.notes}</p>
                  </div>
                )}
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Status</label>
                  <div className="mt-1">{getStatusBadge(selectedAppointment.status)}</div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Submitted</label>
                  <p>{formatDateTime(selectedAppointment.created_at)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;